<template>
  <section class="bg-white" id="pricing">
    <div class="mx-auto flex max-w-7xl flex-col gap-12 px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
      <!-- Header -->
      <div class="mx-auto flex w-full max-w-[720px] flex-col items-center gap-4 text-center">
        <span class="text-sm font-semibold uppercase tracking-wider text-primary-600">Pricing</span>
        <h2 class="text-pretty text-3xl font-semibold tracking-[-0.4px] text-gray-950 sm:text-4xl sm:tracking-[-0.72px] lg:text-balance">
          Simple pricing. No per-postcard fee.
        </h2>
        <p class="text-pretty text-lg text-gray-700">
          Start free, upgrade when you export for real. Yearly billing gives you two months free.
        </p>
        <!-- Billing toggle -->
        <button
          type="button"
          role="switch"
          :aria-checked="billingYearly"
          class="flex items-center gap-3"
          @click="billingYearly = !billingYearly"
        >
          <span
            class="relative flex h-6 w-11 shrink-0 items-center rounded-full p-0.5 transition-colors"
            :class="billingYearly ? 'bg-primary-600' : 'bg-gray-200'"
          >
            <span
              class="size-5 rounded-full bg-white shadow-[0px_1px_3px_0px_rgba(16,24,40,0.1)] transition-transform"
              :class="billingYearly ? 'translate-x-5' : 'translate-x-0'"
            />
          </span>
          <span class="text-sm font-medium text-gray-700">
            Billed yearly
            <span v-if="!billingYearly" class="ml-1.5 rounded-full bg-primary-100 px-2 py-0.5 text-xs font-semibold text-primary-700">2 months free</span>
          </span>
        </button>
      </div>

      <!-- Plan cards -->
      <div class="grid grid-cols-1 gap-6 sm:grid-cols-3">
        <div
          v-for="plan in plans"
          :key="plan.id"
          class="relative flex flex-col gap-6 rounded-2xl border p-6 sm:p-8"
          :class="plan.highlighted
            ? 'border-primary-600 bg-white shadow-[0px_8px_24px_-4px_rgba(127,86,217,0.2)]'
            : 'border-gray-300 bg-white shadow-[0px_1px_3px_0px_rgba(16,24,40,0.06)]'"
        >
          <!-- Highlighted badge -->
          <span
            v-if="plan.badge"
            class="absolute -top-3.5 left-6 inline-flex items-center rounded-full bg-primary-600 px-3 py-1 text-xs font-semibold text-white"
          >
            {{ plan.badge }}
          </span>

          <div>
            <h3 class="text-xl font-semibold text-gray-950">{{ plan.name }}</h3>
            <p class="mt-1 text-sm text-gray-700">{{ plan.tagline }}</p>
          </div>

          <!-- Price -->
          <div class="flex items-end gap-1">
            <span class="text-4xl font-semibold tracking-[-0.72px] text-gray-950">
              ${{ billingYearly ? plan.priceYearly : plan.priceMonthly }}
            </span>
            <span class="mb-1 text-sm text-gray-700">{{ plan.priceSuffix }}</span>
          </div>
          <p v-if="plan.annualTotal && billingYearly" class="text-xs text-gray-600">
            Billed ${{ plan.annualTotal }} a year
          </p>

          <!-- CTA -->
          <Link
            :href="plan.cta"
            class="relative flex items-center justify-center overflow-hidden rounded-lg px-4 py-2.5 text-sm font-semibold shadow-[0px_1px_0px_0px_rgba(16,24,40,0.08)] transition"
            :class="plan.highlighted
              ? 'bg-gradient-to-b from-primary-600 to-primary-700 text-white'
              : 'border border-gray-300 bg-gradient-to-b from-white to-gray-50 text-gray-800'"
          >
            <span
              v-if="plan.highlighted"
              class="pointer-events-none absolute inset-0 rounded-[inherit] shadow-[inset_0px_-2px_0px_0px_rgba(16,24,40,0.2)]"
              aria-hidden="true"
            />
            {{ plan.ctaLabel }}
          </Link>

          <!-- Features -->
          <ul class="flex flex-col gap-2.5">
            <li
              v-for="feat in plan.features"
              :key="feat"
              class="flex items-start gap-2.5 text-sm text-gray-800"
            >
              <svg
                class="mt-0.5 size-4 shrink-0 text-primary-600"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2.5"
                stroke-linecap="round"
                stroke-linejoin="round"
                aria-hidden="true"
              >
                <path d="M5 12l5 5L20 7" />
              </svg>
              {{ feat }}
            </li>
          </ul>
        </div>
      </div>

      <!-- Link to full pricing page -->
      <p class="text-center">
        <Link
          href="/pricing"
          class="text-sm font-semibold text-primary-600 hover:text-primary-700 hover:underline"
        >
          Compare all plans and add-ons →
        </Link>
      </p>
    </div>
  </section>
</template>

<script setup lang="ts">
import { ref } from "vue";
import { Link } from "@inertiajs/vue3";

const billingYearly = ref(false);

const plans = [
  {
    id: "sandbox",
    name: "Sandbox",
    tagline: "Trying it out, developers",
    priceMonthly: 0,
    priceYearly: 0,
    priceSuffix: "forever",
    annualTotal: null,
    badge: null,
    highlighted: false,
    cta: "/signup",
    ctaLabel: "Create free account",
    features: [
      "Full editor, all templates",
      "3 watermarked exports a month",
      "Test API keys",
    ],
  },
  {
    id: "studio",
    name: "Studio",
    tagline: "Marketers and agencies",
    priceMonthly: 49,
    priceYearly: 41,
    priceSuffix: "per month",
    annualTotal: 490,
    badge: null,
    highlighted: false,
    cta: "/signup#plan-studio",
    ctaLabel: "Start with Studio",
    features: [
      "Unlimited exports, no watermark",
      "Merge fields and CSV preview",
      "Send via Lob and PostGrid",
      "3 team seats",
    ],
  },
  {
    id: "platform",
    name: "Platform",
    tagline: "Embedding in your product",
    priceMonthly: 399,
    priceYearly: 333,
    priceSuffix: "per month",
    annualTotal: 3990,
    badge: "For platforms",
    highlighted: true,
    cta: "/signup#plan-platform",
    ctaLabel: "Start with Platform",
    features: [
      "Everything in Studio",
      "White-label embed on your domain",
      "Unlimited end users, SSO tokens",
      "Webhooks, production API keys",
      "Priority support",
    ],
  },
];
</script>
